import React, { useEffect, useRef, useState } from "react";
import { announcements as announcementsApi } from "../api/endpoints";
import type { Announcement, User } from "../types";

const ADMIN_ROLES = ["SuperAdmin", "Admin"];

interface Props {
  user: User;
}

/* ---- Simple Rich Text Editor ---- */
function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const editorRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (editorRef.current && !initialized.current) {
      editorRef.current.innerHTML = value;
      initialized.current = true;
    }
  }, [value]);

  // Reset when value is cleared (new form)
  useEffect(() => {
    if (!value && editorRef.current && initialized.current) {
      editorRef.current.innerHTML = "";
      initialized.current = false;
    }
  }, [value]);

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = prompt("Enter URL:");
    if (url) exec("createLink", url);
  };

  const btnClass = "px-2 py-1 rounded text-sm font-medium hover:bg-slate-200 text-slate-700 transition-colors";

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <button type="button" onClick={() => exec("bold")} className={btnClass} title="Bold">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => exec("italic")} className={btnClass} title="Italic">
          <em>I</em>
        </button>
        <button type="button" onClick={() => exec("underline")} className={btnClass} title="Underline">
          <u>U</u>
        </button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={() => exec("insertUnorderedList")} className={btnClass} title="Bullet list">
          &bull; List
        </button>
        <button type="button" onClick={() => exec("insertOrderedList")} className={btnClass} title="Numbered list">
          1. List
        </button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onClick={insertLink} className={btnClass} title="Insert link">
          Link
        </button>
        <button type="button" onClick={() => exec("removeFormat")} className={btnClass} title="Clear formatting">
          Clear
        </button>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <select
          onChange={(e) => { if (e.target.value) exec("fontSize", e.target.value); e.target.value = ""; }}
          className="text-xs bg-transparent border-0 text-slate-600 cursor-pointer"
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          <option value="1">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">Huge</option>
        </select>
        <select
          onChange={(e) => { if (e.target.value) exec("foreColor", e.target.value); e.target.value = ""; }}
          className="text-xs bg-transparent border-0 text-slate-600 cursor-pointer"
          defaultValue=""
        >
          <option value="" disabled>Color</option>
          <option value="#000000" style={{ color: "#000000" }}>Black</option>
          <option value="#dc2626" style={{ color: "#dc2626" }}>Red</option>
          <option value="#2563eb" style={{ color: "#2563eb" }}>Blue</option>
          <option value="#16a34a" style={{ color: "#16a34a" }}>Green</option>
          <option value="#d97706" style={{ color: "#d97706" }}>Orange</option>
          <option value="#7c3aed" style={{ color: "#7c3aed" }}>Purple</option>
        </select>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="min-h-[160px] max-h-[400px] overflow-y-auto p-3 text-sm text-slate-800 focus:outline-none prose prose-sm max-w-none"
        style={{ whiteSpace: "pre-wrap" }}
      />
    </div>
  );
}

export default function AnnouncementsPage({ user }: Props) {
  const [items, setItems] = useState<Announcement[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ title: "", content: "", priority: 0 });

  const isAdmin = ADMIN_ROLES.includes(user.role?.name || "");

  const load = () => announcementsApi.list().then(setItems);

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.content.trim()) return;
    if (editingId) {
      await announcementsApi.update(editingId, form);
    } else {
      await announcementsApi.create(form);
    }
    setShowForm(false);
    setEditingId(null);
    setForm({ title: "", content: "", priority: 0 });
    load();
  };

  const openEdit = (a: Announcement) => {
    setEditingId(a.id);
    setForm({ title: a.title, content: a.content, priority: a.priority });
    setShowForm(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ title: "", content: "", priority: 0 });
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Announcements</h1>
        {isAdmin && (
          <button onClick={openNew} className="button-primary">
            New Announcement
          </button>
        )}
      </div>

      {items.length === 0 && (
        <p className="text-sm text-slate-500">No announcements at this time.</p>
      )}

      <div className="space-y-4">
        {items.map((a) => (
          <div key={a.id} className="surface-panel p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-lg">{a.title}</h3>
                <div className="text-sm text-slate-600 mt-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: a.content }} />
                <p className="text-xs text-slate-400 mt-3">{new Date(a.created_at).toLocaleDateString()}</p>
              </div>
              {isAdmin && (
                <div className="flex gap-2 shrink-0 ml-4">
                  <button onClick={() => openEdit(a)} className="text-indigo-600 hover:underline text-xs">Edit</button>
                  <button onClick={async () => { await announcementsApi.delete(a.id); load(); }} className="text-rose-600 hover:underline text-xs">Delete</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="surface-panel p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">{editingId ? "Edit Announcement" : "New Announcement"}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-caps mb-1 block">Title</label>
                <input
                  placeholder="Announcement title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input-shell"
                  required
                />
              </div>
              <div>
                <label className="label-caps mb-1 block">Content</label>
                <RichTextEditor
                  value={form.content}
                  onChange={(html) => setForm((f) => ({ ...f, content: html }))}
                />
              </div>
              <div>
                <label className="label-caps mb-1 block">Priority</label>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                  className="input-shell w-24"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="button-ghost">Cancel</button>
                <button type="submit" className="button-primary">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
